import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getEmpleados, getEmpleado, createEmpleado, updateEmpleado, deleteEmpleado, getAFPs, getBancos, getCentrosCosto, getLineasNegocio } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Plus, Pencil, Trash2, Search, UserCheck, UserX, Building2, CreditCard, FileText } from 'lucide-react';

const TIPOS_DOC = [
  { value: 'DNI', label: 'DNI' },
  { value: 'CE', label: 'Carnet Extranjería' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
];

const TIPOS_TRABAJADOR = [
  { value: 'EMPLEADO', label: 'Empleado' },
  { value: 'OBRERO', label: 'Obrero' },
  { value: 'PRACTICANTE', label: 'Practicante' },
  { value: 'LOCADOR', label: 'Locador' },
];

const REGIMENES_LABORALES = [
  { value: 'GENERAL', label: 'Régimen General' },
  { value: 'MYPE', label: 'MYPE / Pequeña Empresa' },
  { value: 'AGRARIO', label: 'Agrario' },
  { value: 'CONSTRUCCION', label: 'Construcción Civil' },
];

const REGIMENES_PENSIONARIOS = [
  { value: 'AFP', label: 'AFP' },
  { value: 'ONP', label: 'ONP' },
  { value: 'SIN_REGIMEN', label: 'Sin Régimen' },
];

const TIPOS_COMISION = [
  { value: 'FLUJO', label: 'Comisión Flujo' },
  { value: 'MIXTA', label: 'Comisión Mixta' },
];

const FORMAS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'BANCO', label: 'Banco' },
  { value: 'MIXTO', label: 'Mixto' },
];

const INITIAL_FORM = {
  codigo: '',
  tipo_doc: 'DNI',
  nro_doc: '',
  apellido_paterno: '',
  apellido_materno: '',
  nombres: '',
  fecha_nacimiento: '',
  sexo: 'M',
  direccion: '',
  telefono: '',
  email: '',
  fecha_ingreso: new Date().toISOString().split('T')[0],
  fecha_cese: '',
  cargo: '',
  area: '',
  centro_costo_id: '',
  linea_negocio_id: '',
  tipo_trabajador: 'EMPLEADO',
  regimen_laboral: 'GENERAL',
  regimen_pensionario: 'AFP',
  afp_id: '',
  tipo_comision: 'FLUJO',
  cuspp: '',
  sueldo_real: '',
  sueldo_planilla: '',
  asignacion_familiar: false,
  porcentaje_beneficios: '100',
  banco_id: '',
  nro_cuenta: '',
  cci: '',
  forma_pago: 'BANCO',
  activo: true
};

const EmpleadosPage = () => {
  const { empresaActual } = useApp();
  const [empleados, setEmpleados] = useState([]);
  const [afps, setAFPs] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [lineasNegocio, setLineasNegocio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroActivo, setFiltroActivo] = useState('all');
  const [busqueda, setBusqueda] = useState('');
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const [empleadosRes, afpsRes, bancosRes, ccRes, lnRes] = await Promise.all([
        getEmpleados(empresaActual.id),
        getAFPs(),
        getBancos(),
        getCentrosCosto(empresaActual.id),
        getLineasNegocio(empresaActual.id)
      ]);
      setEmpleados(empleadosRes.data);
      setAFPs(afpsRes.data);
      setBancos(bancosRes.data);
      setCentrosCosto(ccRes.data);
      setLineasNegocio(lnRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNew = () => {
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setModalOpen(true);
  };

  const handleEdit = async (emp) => {
    setEditingId(emp.id);
    setFormData({
      codigo: emp.codigo || '',
      tipo_doc: emp.tipo_doc || 'DNI',
      nro_doc: emp.nro_doc || '',
      apellido_paterno: emp.apellido_paterno || '',
      apellido_materno: emp.apellido_materno || '',
      nombres: emp.nombres || '',
      fecha_nacimiento: emp.fecha_nacimiento || '',
      sexo: emp.sexo || 'M',
      direccion: emp.direccion || '',
      telefono: emp.telefono || '',
      email: emp.email || '',
      fecha_ingreso: emp.fecha_ingreso || '',
      fecha_cese: emp.fecha_cese || '',
      cargo: emp.cargo || '',
      area: emp.area || '',
      centro_costo_id: emp.centro_costo_id?.toString() || '',
      linea_negocio_id: emp.linea_negocio_id?.toString() || '',
      tipo_trabajador: emp.tipo_trabajador || 'EMPLEADO',
      regimen_laboral: emp.regimen_laboral || 'GENERAL',
      regimen_pensionario: emp.regimen_pensionario || 'AFP',
      afp_id: emp.afp_id?.toString() || '',
      tipo_comision: emp.tipo_comision || 'FLUJO',
      cuspp: emp.cuspp || '',
      sueldo_real: emp.sueldo_real?.toString() || '',
      sueldo_planilla: emp.sueldo_planilla?.toString() || '',
      asignacion_familiar: emp.asignacion_familiar || false,
      porcentaje_beneficios: emp.porcentaje_beneficios?.toString() || '100',
      banco_id: emp.banco_id?.toString() || '',
      nro_cuenta: emp.nro_cuenta || '',
      cci: emp.cci || '',
      forma_pago: emp.forma_pago || 'BANCO',
      activo: emp.activo !== false
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desea desactivar este empleado?')) return;
    try {
      await deleteEmpleado(id);
      toast.success('Empleado desactivado');
      loadData();
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const handleSubmit = async () => {
    if (!formData.nro_doc || !formData.apellido_paterno || !formData.nombres || !formData.fecha_ingreso) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        empresa_id: empresaActual.id,
        centro_costo_id: formData.centro_costo_id ? parseInt(formData.centro_costo_id) : null,
        linea_negocio_id: formData.linea_negocio_id ? parseInt(formData.linea_negocio_id) : null,
        afp_id: formData.afp_id ? parseInt(formData.afp_id) : null,
        banco_id: formData.banco_id ? parseInt(formData.banco_id) : null,
        sueldo_real: parseFloat(formData.sueldo_real) || 0,
        sueldo_planilla: parseFloat(formData.sueldo_planilla) || 0,
        porcentaje_beneficios: parseFloat(formData.porcentaje_beneficios) || 100,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        fecha_cese: formData.fecha_cese || null
      };

      if (editingId) {
        await updateEmpleado(editingId, payload);
        toast.success('Empleado actualizado');
      } else {
        await createEmpleado(payload);
        toast.success('Empleado creado');
      }
      setModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Filtrar empleados
  const empleadosFiltrados = empleados.filter(emp => {
    const matchActivo = filtroActivo === 'all' || 
      (filtroActivo === 'activos' && emp.activo) || 
      (filtroActivo === 'inactivos' && !emp.activo);
    
    const matchBusqueda = !busqueda || 
      emp.nombres?.toLowerCase().includes(busqueda.toLowerCase()) ||
      emp.apellido_paterno?.toLowerCase().includes(busqueda.toLowerCase()) ||
      emp.nro_doc?.includes(busqueda);
    
    return matchActivo && matchBusqueda;
  });

  const stats = {
    total: empleados.length,
    activos: empleados.filter(e => e.activo).length,
    inactivos: empleados.filter(e => !e.activo).length
  };

  return (
    <div className="space-y-6" data-testid="empleados-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-indigo-600" />
            Empleados
          </h1>
          <p className="text-gray-500 text-sm mt-1">Gestión de personal y datos de planilla</p>
        </div>
        <Button onClick={handleNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="nuevo-empleado-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Empleado
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-indigo-100 rounded-full">
              <Users className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-full">
              <UserCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Activos</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.activos}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-gray-50 to-white border-gray-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-gray-100 rounded-full">
              <UserX className="w-6 h-6 text-gray-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cesados</p>
              <p className="text-2xl font-bold text-gray-500">{stats.inactivos}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros y búsqueda */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nombre o documento..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroActivo} onValueChange={setFiltroActivo}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="activos">Solo Activos</SelectItem>
                <SelectItem value="inactivos">Solo Cesados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : empleadosFiltrados.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No hay empleados registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Nombre Completo</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Régimen</TableHead>
                  <TableHead className="text-right">Sueldo Real</TableHead>
                  <TableHead className="text-right">Sueldo Planilla</TableHead>
                  <TableHead>Beneficios</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {empleadosFiltrados.map((emp) => (
                  <TableRow key={emp.id} className={!emp.activo ? 'bg-gray-50 opacity-60' : ''}>
                    <TableCell className="font-mono text-sm">{emp.codigo || '-'}</TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-500">{emp.tipo_doc}</span>
                      <br />
                      <span className="font-medium">{emp.nro_doc}</span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{emp.apellido_paterno} {emp.apellido_materno}</div>
                      <div className="text-sm text-gray-500">{emp.nombres}</div>
                    </TableCell>
                    <TableCell>{emp.cargo || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {emp.regimen_laboral === 'MYPE' ? 'MYPE' : emp.regimen_laboral}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">
                      S/. {emp.sueldo_real?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      S/. {emp.sueldo_planilla?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <Badge className={emp.porcentaje_beneficios === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {emp.porcentaje_beneficios}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={emp.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                        {emp.activo ? 'Activo' : 'Cesado'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(emp)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {emp.activo && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(emp.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              {editingId ? 'Editar Empleado' : 'Nuevo Empleado'}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="personal" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="personal" className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Personal
              </TabsTrigger>
              <TabsTrigger value="laboral" className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                Laboral
              </TabsTrigger>
              <TabsTrigger value="pension" className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                Pensión
              </TabsTrigger>
              <TabsTrigger value="pago" className="flex items-center gap-1">
                <CreditCard className="w-4 h-4" />
                Pago
              </TabsTrigger>
            </TabsList>

            {/* Tab Personal */}
            <TabsContent value="personal" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Código</Label>
                  <Input 
                    value={formData.codigo} 
                    onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                    placeholder="EMP001"
                  />
                </div>
                <div>
                  <Label>Tipo Doc. *</Label>
                  <Select value={formData.tipo_doc} onValueChange={(v) => setFormData({...formData, tipo_doc: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS_DOC.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>N° Documento *</Label>
                  <Input 
                    value={formData.nro_doc} 
                    onChange={(e) => setFormData({...formData, nro_doc: e.target.value})}
                    placeholder="12345678"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Apellido Paterno *</Label>
                  <Input 
                    value={formData.apellido_paterno} 
                    onChange={(e) => setFormData({...formData, apellido_paterno: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Apellido Materno</Label>
                  <Input 
                    value={formData.apellido_materno} 
                    onChange={(e) => setFormData({...formData, apellido_materno: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Nombres *</Label>
                  <Input 
                    value={formData.nombres} 
                    onChange={(e) => setFormData({...formData, nombres: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Fecha Nacimiento</Label>
                  <Input 
                    type="date"
                    value={formData.fecha_nacimiento} 
                    onChange={(e) => setFormData({...formData, fecha_nacimiento: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Sexo</Label>
                  <Select value={formData.sexo} onValueChange={(v) => setFormData({...formData, sexo: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Dirección</Label>
                  <Input 
                    value={formData.direccion} 
                    onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input 
                    value={formData.telefono} 
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Tab Laboral */}
            <TabsContent value="laboral" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Fecha Ingreso *</Label>
                  <Input 
                    type="date"
                    value={formData.fecha_ingreso} 
                    onChange={(e) => setFormData({...formData, fecha_ingreso: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Fecha Cese</Label>
                  <Input 
                    type="date"
                    value={formData.fecha_cese} 
                    onChange={(e) => setFormData({...formData, fecha_cese: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Tipo Trabajador</Label>
                  <Select value={formData.tipo_trabajador} onValueChange={(v) => setFormData({...formData, tipo_trabajador: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS_TRABAJADOR.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cargo</Label>
                  <Input 
                    value={formData.cargo} 
                    onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                    placeholder="Ej: Operario, Asistente..."
                  />
                </div>
                <div>
                  <Label>Área</Label>
                  <Input 
                    value={formData.area} 
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    placeholder="Ej: Producción, Ventas..."
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Centro de Costo</Label>
                  <Select value={formData.centro_costo_id} onValueChange={(v) => setFormData({...formData, centro_costo_id: v === 'none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {centrosCosto.map(cc => <SelectItem key={cc.id} value={cc.id.toString()}>{cc.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Línea de Negocio</Label>
                  <Select value={formData.linea_negocio_id} onValueChange={(v) => setFormData({...formData, linea_negocio_id: v === 'none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {lineasNegocio.map(ln => <SelectItem key={ln.id} value={ln.id.toString()}>{ln.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Régimen Laboral</Label>
                  <Select value={formData.regimen_laboral} onValueChange={(v) => setFormData({...formData, regimen_laboral: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REGIMENES_LABORALES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>% Beneficios</Label>
                  <Select value={formData.porcentaje_beneficios} onValueChange={(v) => setFormData({...formData, porcentaje_beneficios: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100">100% (Régimen General)</SelectItem>
                      <SelectItem value="50">50% (Pequeña Empresa)</SelectItem>
                      <SelectItem value="0">0% (Sin beneficios)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sueldo Real (lo que recibe)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.sueldo_real} 
                    onChange={(e) => setFormData({...formData, sueldo_real: e.target.value})}
                    placeholder="2000.00"
                    className="text-right"
                  />
                </div>
                <div>
                  <Label>Sueldo en Planilla (para cálculos)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.sueldo_planilla} 
                    onChange={(e) => setFormData({...formData, sueldo_planilla: e.target.value})}
                    placeholder="1500.00"
                    className="text-right"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="asig_fam"
                  checked={formData.asignacion_familiar}
                  onCheckedChange={(v) => setFormData({...formData, asignacion_familiar: v})}
                />
                <Label htmlFor="asig_fam">Asignación Familiar (10% RMV)</Label>
              </div>
            </TabsContent>

            {/* Tab Pensión */}
            <TabsContent value="pension" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Régimen Pensionario</Label>
                  <Select value={formData.regimen_pensionario} onValueChange={(v) => setFormData({...formData, regimen_pensionario: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REGIMENES_PENSIONARIOS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {formData.regimen_pensionario === 'AFP' && (
                  <div>
                    <Label>AFP</Label>
                    <Select value={formData.afp_id} onValueChange={(v) => setFormData({...formData, afp_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar AFP..." /></SelectTrigger>
                      <SelectContent>
                        {afps.map(a => <SelectItem key={a.id} value={a.id.toString()}>{a.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {formData.regimen_pensionario === 'AFP' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo Comisión</Label>
                    <Select value={formData.tipo_comision} onValueChange={(v) => setFormData({...formData, tipo_comision: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS_COMISION.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>CUSPP</Label>
                    <Input 
                      value={formData.cuspp} 
                      onChange={(e) => setFormData({...formData, cuspp: e.target.value})}
                      placeholder="Código único AFP"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Tab Pago */}
            <TabsContent value="pago" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Forma de Pago</Label>
                  <Select value={formData.forma_pago} onValueChange={(v) => setFormData({...formData, forma_pago: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FORMAS_PAGO.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(formData.forma_pago === 'BANCO' || formData.forma_pago === 'MIXTO') && (
                  <div>
                    <Label>Banco</Label>
                    <Select value={formData.banco_id} onValueChange={(v) => setFormData({...formData, banco_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar banco..." /></SelectTrigger>
                      <SelectContent>
                        {bancos.map(b => <SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {(formData.forma_pago === 'BANCO' || formData.forma_pago === 'MIXTO') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>N° Cuenta</Label>
                    <Input 
                      value={formData.nro_cuenta} 
                      onChange={(e) => setFormData({...formData, nro_cuenta: e.target.value})}
                      placeholder="Número de cuenta"
                    />
                  </div>
                  <div>
                    <Label>CCI</Label>
                    <Input 
                      value={formData.cci} 
                      onChange={(e) => setFormData({...formData, cci: e.target.value})}
                      placeholder="Código interbancario"
                    />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 pt-4">
                <Checkbox 
                  id="activo"
                  checked={formData.activo}
                  onCheckedChange={(v) => setFormData({...formData, activo: v})}
                />
                <Label htmlFor="activo">Empleado Activo</Label>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpleadosPage;
